import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PercentPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  effect,
  output,
  viewChild
} from '@angular/core';
import { Validators } from '@angular/forms';
import { DUAL_CURRENCY_DEPOSIT } from '@approot/constants';
import {
  DefaultHedges,
  DiscountFactorResponse,
  FieldsToBeEdited,
  GenericLeg,
  Leg,
  LegPrice,
  LegSyncingFields,
  Payload,
  Pricing,
  ProductName,
  QuotePair,
  RFQ_STATUS_ERROR,
  RFQ_STATUS_WAITING,
  RFQ_TOP_OF_BOOK,
  RFQ_VIEW_CHANGE,
  ReportingCurrencyStateKey,
  ResultPerLeg,
  RfqActionData,
  RfqLegSettings,
  RfqProductApiName,
  RfqProductGrouping,
  RfqSettingsState,
  RfqSettingsStateKey,
  RfqSetup,
  RfqTab,
  RfqTabLeg
} from '@approot/models';
import {
  AdminService,
  BookingTradeState,
  ConfigService,
  ExecutionContext,
  InitRfqApiCalls,
  LegService,
  LegSyncService,
  PricingExtraService,
  ProductsConfigService,
  RfqService,
  RfqTabService,
  RfqValidationStatus,
  UserStorageService,
  ValidateRfq,
  VolBasedRfqOptionPriceV2Result,
  canDisplayDF
} from '@approot/services';
import {
  getTabTitleCross,
  rfqLegs,
  rfqTabTitleMapper,
  setPositionToUse
} from '@approot/services/rfq/rfq-creation';
import {
  Margin,
  addRemoveVolSpread,
  changeInCross,
  enableDisableMarginTools,
  hasFieldsChanged,
  indicativeQuoteMaker,
  legComponentsToRfqBuilderLegs,
  marginEditableFields,
  positionsHandler,
  rfqLegAttributeDefaults,
  rfqProductApiName,
  updateMarginActiveAttributes,
  v2ToQuoteMap,
  volBasedEditableFields,
  volBasedSpotFeedEditableFields
} from '@approot/services/rfq/rfq-tab/rfq-tab';
import { openClose } from '@approot/shared/animations';
import { SubscriptionSink, Utilities, clone, sleep } from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import { getAttributeControlFromKey } from '@approot/widgets/strategy-detail/leg/utilities/leg-attribute-utilities';
import { onMarginStyleChange } from '@approot/widgets/strategy-detail/leg/utilities/leg.utilities';
import { ClrCombobox } from '@clr/angular';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { Debounce, Throttle } from 'lodash-decorators';
import {
  EMPTY,
  Observable,
  Subscription,
  firstValueFrom,
  forkJoin,
  of,
  throwError
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  shareReplay,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import { RfqBookTradesComponent } from './rfq-book-trades/rfq-book-trades.component';
import { RfqPricesComponent } from './rfq-prices/rfq-prices.component';
import { RfqSetupComponent } from './rfq-setup/rfq-setup.component';
import { RfqSetupService } from './rfq-setup/rfq-setup.service';

@Component({
  selector: 'app-rfq-tab',
  templateUrl: './rfq-tab.component.html',
  styleUrls: ['./rfq-tab.component.scss'],
  providers: [LegService, LegSyncService],
  animations: [openClose]
})
export class RfqTabComponent implements OnInit, OnDestroy, AfterViewInit {
  public bookTradesCmpt = viewChild(RfqBookTradesComponent);
  @ViewChild(RfqPricesComponent, { static: false })
  public rfqPricesComponent: RfqPricesComponent;
  @ViewChild(RfqSetupComponent, { static: false })
  public rfqSetupComponent: RfqSetupComponent;
  @ViewChildren(LegComponent) public legComponents: QueryList<LegComponent>;
  @Input() public tabData: RfqTab;
  @Input() parentTabIndex: number;
  @Output() public toContainerCommunicator = new EventEmitter();
  @Output() public saveDimensions = new EventEmitter();
  @Output() public autoResizeComponent = new EventEmitter<{
    numOfProviders: number;
    isVolBased: boolean;
  }>();
  public resetRfqContainerHeight = output();
  public legs: Leg[];
  public rfqExecutionSettings: RfqLegSettings = {
    rfqLeg: true,
    static: true,
    editableSettings: { editableFields: [], lockWhileActive: [] }
  };
  public productGroupings: RfqProductGrouping[];
  public defaultFieldsToBeEdited: FieldsToBeEdited = {
    notional: 'counterNotional',
    notional1: 'counterNotional',
    notional2: 'counterNotional'
  };
  public tabLoaded = false;
  public crossFilter = '';
  public rfqPricingMessages: RfqValidationStatus = {
    messages: '',
    isWarning: false,
    isError: false
  };
  public legSyncingInfo: LegSyncingFields = {
    spot: true,
    forwardPoints: true,
    premiumValueType: false,
    premiumSettlementDate: false
  };
  public failedExecutionContext = false;
  public blockVolBasedPriceHedgeChanges = false;
  public indicativePremiumStyle$: Observable<string>;
  public forwardPointsMultiplier: number;
  public pipsHighlightedIndices: number[] = [];
  public bookingState: BookingTradeState = null;
  public showLegs: boolean = true;
  public isGenericMultiHedge: boolean = false;
  private legComponentsArray: LegComponent[];
  private totalLegsLoaded = 0;
  private legInputChangeMade = false;
  private legInputsFocused = false;
  private subSink = new SubscriptionSink();
  private currentCopyOfLegs: GenericLeg[];
  private readonly twoWayText = 'Two-way';
  private portfolioResults: Pricing;
  private strategyPriced: Payload;
  private legsApiCallSub: Subscription;
  private resultPerLeg: ResultPerLeg[];
  private previousChildLegs: GenericLeg[];
  private firstLegListener = new SubscriptionSink();
  private prevRfqProductApiName: RfqProductApiName;
  private pricingSub: Subscription;
  private validationSub: Subscription;
  private providers: string[];
  private hedge: DefaultHedges;
  private updateVolBasedQuoteListeners = true;
  private currVolBasedOptionProducts: GenericLeg[];
  private salesMarginInitialized = false;
  constructor(
    private rfqService: RfqService,
    private legService: LegService,
    public rfqTabService: RfqTabService,
    private percentPipe: PercentPipe,
    private productsConfigService: ProductsConfigService,
    private adminService: AdminService,
    private elementRef: ElementRef,
    private legSyncService: LegSyncService,
    private pricingExtraService: PricingExtraService,
    private renderer: Renderer2,
    private userStorageService: UserStorageService,
    private rfqSetupService: RfqSetupService,
    private configService: ConfigService
  ) {
    this.legService.productSpecifications = clone(
      this.productsConfigService.productInfo.productSpecifications
    );

    effect(_ => {
      this.forwardPointsMultiplier =
        this.rfqTabService.forwardPointsMultiplier();
      switch (this.forwardPointsMultiplier) {
        case 100:
          this.pipsHighlightedIndices = [0, 1];
          break;
        case 10_000:
          this.pipsHighlightedIndices = [2, 3];
          break;
        default:
          this.pipsHighlightedIndices = [];
          break;
      }
    });

    effect(() => {
      this.rfqTabService.rfqCommentSignal();
      if (this.rfqTabService.selectedLegs) {
        this.debouncedUpdateNewVolBasedQuoteListeners();
      }
    });
  }

  public async ngOnInit() {
    if (!this.configService.userSettings().isWhiteLabelUser) {
      this.rfqTabService.accounts.update(
        _ => this.rfqService.initialEntityAccounts
      );
    }
    if (this.tabData.static) {
      try {
        const r = await this.getExecutionContext();
        const executionType = r.executionTypes[0].shortName; // KEEP [0] FOR NOW
        this.isGenericMultiHedge = executionType === 'genericMultiHedge';

        this.tabData.isVolBased = executionType === 'volBased';
        this.tabData.isPremiumBasedVanillasWithSpotFeed =
          executionType === 'premiumBasedVanillasWithSpotFeed';
        this.tabData.executionContext = r;
      } catch (error) {
        console.error(error);
        this.failedExecutionContext = true;
        return;
      }
    } else {
      this.tabData.isVolBased = this.rfqService.isVolBased;
      this.tabData.isPremiumBasedVanillasWithSpotFeed =
        this.rfqService.isPremiumBasedVanillasWithSpotFeed;
      this.tabData.executionContext = this.rfqService.executionContext;
    }

    this.rfqSetupService.setHedges(this.tabData);

    if (this.tabData.executionContext.additionalRfqAttributes) {
      this.rfqSetupService.additionalRfqAttributes.set(
        clone(this.tabData.executionContext.additionalRfqAttributes)
      );
    }
    this.rfqSetupService.createSetupForm(
      this.userStorageService.getItem<RfqSettingsState>(RfqSettingsStateKey),
      this.adminService.medusaAccount.entity,
      this.tabData.isVolBased,
      this.rfqService.isLegacyWhitelabel(),
      this.tabData.executionContext,
      this.adminService.userInfo.isWhiteLabelUser
    );
    const indicativePremiumStyle =
      this.rfqSetupService.setupForm.controls.indicativePremiumStyle;
    if (indicativePremiumStyle) {
      this.indicativePremiumStyle$ = indicativePremiumStyle.valueChanges.pipe(
        shareReplay(1)
      );
    }

    this.blockVolBasedPriceHedgeChanges =
      !!this.tabData.presetSetupData?.hedges;
    this.handleTabInit();
    this.listenToHedgesChanges();
    this.tabLoaded = true;
  }

  public ngOnDestroy() {
    this.rfqTabService.destroy();
    this.subSink.unsubscribe();
    this.cancelAllApiCalls();
  }

  public ngAfterViewInit(): void {
    this.legComponentsArray = this.legComponents.toArray();
    this.handleLegSyncing();
    this.subSink.sink = this.legComponents.changes.subscribe(legComponents => {
      this.strategyPriced = null;
      this.rfqTabService.createDtoResponse = null;
      this.legComponentsArray = legComponents.toArray();
      this.currentCopyOfLegs = this.makeCopyOfLegs;
      this.rfqTabService.selectedLegs = this.legComponentsArray;
      this.handleLegSyncing();
      this.setRfqTabProductName();
      this.reSyncLegs();
      this.cancelAllApiCalls();
      this.updatePositionInLegs();
      this.updateOnLegChange();
      this.wipeAllIndicativeQuotes();
      this.wipeQuotes();
      this.listenToFirstLeg();
      const containsDcd = legComponents.find(
        legComponent => legComponent.leg.productType === DUAL_CURRENCY_DEPOSIT
      );
      enableDisableMarginTools(
        this.legComponentsArray,
        this.configService.userSettings().useWhiteLabelMargin,
        this.rfqExecutionSettings.editableSettings,
        this.rfqTab.static,
        containsDcd
      );

      updateMarginActiveAttributes(
        this.legService.activeAttributes(),
        !!containsDcd,
        this.configService.userSettings().useWhiteLabelMargin
      );
      this.tabData.useWhiteLabelMargin =
        this.configService.userSettings().useWhiteLabelMargin && !containsDcd;
      if (!this.legComponentsArray.length) {
        this.tabData.title.productName = null;
        this.tabData.title.quotePair = null;
      }

      if (!this.tabData.static && this.rfqTabService.providers?.length) {
        this.autoResizeComponent.emit({
          numOfProviders: this.rfqTabService.providers.length,
          isVolBased: this.tabData.isVolBased
        });
      }

      this.rfqSetupService.updateHedges(
        clone(this.rfqSetupService.defaultHedges)
      );
      this.rfqSetupComponent?.removeUnavailableFields();
    });

    if (!this.tabData.static) {
      // the add leg button should be enabled by default
      this.qs('.rfq-tab__leg-control-panel__add-leg').click();
    }

    this.subSink.sink = this.rfqTabService.onRfqSessionEnded.subscribe(_ => {
      /* if the rfq ends there are cases where we need to
      revalidate before the next session starts */
      if (this.rfqTabService.setupMultiHedgesValidating) {
        this.reValidateRfq(true);
      }
    });
  }

  private handleLegSyncing(): void {
    if (this.legSyncingInfo.premiumValueType) {
      this.legSyncService.setupLegPreiumValueSync(
        this.legComponentsArray.map(k => k.form.get('premiumValueType')?.value)
      );
    }

    if (this.legSyncingInfo.premiumSettlementDate) {
      this.legSyncService.setupPremiumSettlementDateSync(
        this.legComponentsArray.map(
          k => k.form.get('premiumSettlementDate')?.value
        )
      );
    }
  }

  public addProduct(
    product: string,
    isNewLeg: boolean,
    presetPosition?: RfqTabLeg
  ): void {
    const productName = product.replace(/\s+/g, '').toLowerCase();
    const rfqSavedSettings = this.rfqSettingsState();

    if (!this.tabData.static && this.adminService.useDefaults) {
      // add the default values to the preset
      let currentDefaults = this.adminService.attributeDefaults;
      presetPosition = presetPosition || {};
      currentDefaults.forEach(x => {
        let shortName =
          this.productsConfigService.productInfo.attributesDisplayList.filter(
            y => y.longName === x.key
          )[0]?.shortName;

        if (shortName === 'cross' && presetPosition.cross) {
          return;
        }
        presetPosition[shortName] = x.value;
      });
    }

    const originalSpec = this.legService.getProductSpec(productName);

    const extraAttributesOptions = {
      productSpec: originalSpec,
      isVolBased: this.tabData.isVolBased,
      addDf:
        this.tabData.isVolBased &&
        canDisplayDF(originalSpec.productType.shortName),
      isProviderQuote: false,
      userSettings: this.configService.userSettings(),
      removeSpot: this.rfqTabService.tabData.executionContext.useSpotInRfqPanel,
      addWhiteLabelAttributes: true
    };

    let newProductSpec = this.productsConfigService.addExtraRfqAttributes(
      extraAttributesOptions
    );
    newProductSpec =
      this.productsConfigService.addExtraAttributeValidators(newProductSpec);

    if (presetPosition) {
      presetPosition = {
        ...rfqLegAttributeDefaults(
          rfqSavedSettings['leg-attributes'] || {},
          newProductSpec,
          this.legComponentsArray
        ),
        ...presetPosition
      };
    }
    this.legService.addLeg({
      productName,
      newProduct: isNewLeg,
      preSetPosition: presetPosition,
      tradeViewMode: false,
      productSpec: newProductSpec
    });
    this.crossFilter = '';
  }

  public onLegPresetLoaded(index: number): void {
    // when all the legs have been loaded
    // store all the legcomponents
    // save the dimensions
    setTimeout(() => {
      this.handleStaticNewLegs(index);
      if (!this.tabData.static) {
        this.rfqTabService.setReadOnlyOnFields(
          false,
          this.rfqExecutionSettings.editableSettings.editableFields
        );
      } else if (this.tabData.strategyBrokenDown) {
        this.getLatestFwdsVolsOnEmpty(index);
      }

      this.listenToInputs(index);
      if (
        this.tabData.static &&
        this.totalLegsLoaded === this.tabData.legs.length
      ) {
        this.onChangeInLegs();
      }
    }, 0);
  }

  public numOfProvidersAvailable(numOfProviders: number): void {
    this.autoResizeComponent.emit({
      numOfProviders: numOfProviders,
      isVolBased: this.tabData.isVolBased
    });
  }

  @Throttle(1000)
  public get isRunning(): boolean {
    return this.rfqTabService.rfqRunning();
  }

  public updateOnLegChange(ignoreFocused?: boolean): void {
    setTimeout(_ => {
      if (this.rfqTabService.rfqRunning() && !this.legInputsFocused) {
        this.adjustQuoteForMargin();
      }
    }, 0);

    if (
      this.rfqTabService.rfqRunning() ||
      (this.tabData.static && !this.providers)
    ) {
      return;
    }
    this.onChangeInLegs(ignoreFocused);
  }

  public pricerToMainTabCommunicator(data: RfqActionData): void {
    switch (data.command) {
      case RFQ_TOP_OF_BOOK:
        this.toContainerCommunicator.emit(data);
        break;
      case RFQ_VIEW_CHANGE:
        this.updatePositionInLegs();
        break;
    }
  }

  public drop(ev: CdkDragDrop<Leg[]>): void {
    moveItemInArray(ev.container.data, ev.previousIndex, ev.currentIndex);
  }

  public resetRfqContainerHeightFunc(): void {
    this.resetRfqContainerHeight.emit();
  }

  public removeIndicativePrices(): void {
    this.pricingSub?.unsubscribe();
    this.rfqTabService.indicativeQuotes.update(state => {
      state.bid.loading = false;
      state.offer.loading = false;
      state.bid.quote = null;
      state.offer.quote = null;
      return state;
    });
  }

  public onChangeInLegs(ignoreFocused?: boolean): void {
    setTimeout(_ => {
      if (!this.legComponentsArray.length) {
        this.legsApiCallSub?.unsubscribe();
        return;
      }

      // LODH/WL don't have providers
      const providerCheck =
        this.providers !== undefined || this.rfqService.isLegacyWhitelabel();
      if (
        (this.legInputsFocused && !ignoreFocused) ||
        (this.tabData.static && !providerCheck)
      ) {
        // if an input is focused then we dont want to allow an rfq
        // providers has to be loaded before carrying on if the tab is static
        return;
      }

      this.rfqTabService.legsValid = !this.legComponentsArray.find(
        legComponent => {
          const positionControl = legComponent.form.get('position');
          return (
            !legComponent.form.valid ||
            !legComponent.legHelperService.presetsLoaded ||
            (positionControl && !legComponent.leg.position)
          );
        }
      );

      if (!this.rfqTabService.legsValid) {
        return;
      }

      const marketFixingValueChanged =
        !this.rfqTabService.medusaCodeAndType || this.isChangeInMarketFixing;
      const prevProduct0 = this.strategyPriced?.products[0];
      const prevCross = prevProduct0?.cross;
      const prevProduct = this.tabData.title.productName?.shortName;
      this.currentCopyOfLegs = this.makeCopyOfLegs;
      let strategyToUse = this.getStrategy;

      if (isEqual(this.strategyPriced, strategyToUse)) {
        /* if we are pricing the same strategy as previously
        do not re-price legs */
        return;
      }

      const hasDatesChanged = this.hasDatesChangedFn(
        strategyToUse.products,
        this.strategyPriced?.products
      );

      const productName = this.setRfqTabProductName();
      this.strategyPriced = strategyToUse;
      const currProduct0 = this.strategyPriced.products[0];
      const isDcd = productName?.shortName === 'dcd';
      const shouldCreateDto = this.shouldCreateDtoFn(prevProduct, prevCross);
      const changeInCurrencyPair = changeInCross(prevProduct0, currProduct0);
      this.rfqTabService.setForwardPointsMultiplier();
      if (isDcd) {
        if (
          this.tabData.static &&
          this.legComponentsArray.every(x => x.leg.pricedLeg)
        ) {
          // prevents static dcd's from being re-priced
          return;
        }
        this.legComponentsArray.forEach((leg, index: number) => {
          leg.legPricingService.pricingDcd(strategyToUse, index);
        });
      } else {
        this.setRfqTabLegs();
      }

      this.wipeAllIndicativeQuotes();
      this.rfqTabService.rfqRunning.update(_ => false);
      this.rfqTabService.changeTabTile(RFQ_STATUS_WAITING, null);
      this.handleApiCalls(
        shouldCreateDto,
        marketFixingValueChanged,
        isDcd,
        hasDatesChanged,
        changeInCurrencyPair
      );
    }, 0);
  }

  public crossKeydown(): void {
    const newLegValue = this.crossFilter.split('|');
    this.addProduct(newLegValue[0], true, { cross: newLegValue[1] });
    this.qs('.rfq-tab').click();
  }

  public focusCrossDropdown(productName: string): void {
    setTimeout(() => {
      const menu = this.qs(`.${productName}-menu`);
      if (!menu?.className.includes('is-off-screen')) {
        this.qs(`.cross-combobox.${productName} button`, menu).click();
      }
    }, 0);
  }

  public menuCrossInputChange(combobox: ClrCombobox<any>): void {
    combobox.focusFirstActive();
  }

  public blurCurrentElement(index: number): void {
    const elem = document.activeElement as HTMLElement;
    const control = getAttributeControlFromKey(
      elem.id,
      this.legComponentsArray[index].leg.attributesAsFormControls
    );

    if (control && control.controlType !== 'dropdown') {
      elem.blur();
    }
  }

  public showHideLegs(): void {
    this.showLegs = !this.showLegs;
  }

  public onProvidersChange(providers: string[]) {
    if (
      !this.providers &&
      (this.tabData.title.productName?.shortName === 'dcd' ||
        this.tabData.static)
    ) {
      this.providers = providers;
      this.onChangeInLegs();
      return;
    }

    this.providers = providers;
    if (!providers.length || !this.rfqTabService.legsValid) {
      return;
    }
    this.reValidateRfq();
  }

  public premiumValueChange(premiumValue: string): void {
    if (this.rfqTabService.rfqRunning()) {
      return;
    }
    this.createIndicativeQuotes({
      ...this.rfqSetupService.setupForm.value,
      premiumValue
    });
  }

  public onBookingStateChange(state: BookingTradeState) {
    this.bookingState = state;
    this.rfqTabService.warnOnClose = state !== 'BOOKED';
  }

  /*<PRIVATE>************************************************************/

  @Debounce(100)
  private adjustQuoteForMargin(): void {
    this.rfqTabService.adjustQuoteForMargin();
  }

  private getLatestFwdsVolsOnEmpty(legIndex: number): void {
    this.getLatestFwdIfEmpty(legIndex);
    this.getLatestVolsIfEmpty(legIndex);
  }

  private getLatestVolsIfEmpty(legIndex: number): void {
    const legComponent = this.legComponentsArray[legIndex];
    const form = legComponent.form;
    const bidField = 'volBid';
    const askField = 'volAsk';
    const bidCtrl = form.get(bidField);
    const askCtrl = form.get(askField);
    const emptyVols =
      bidCtrl &&
      !Utilities.isNotBlank(bidCtrl.value) &&
      askCtrl &&
      !Utilities.isNotBlank(askCtrl.value);
    if (emptyVols) {
      legComponent.legFwdVolService.calcImpliedVolatility('');
    }
  }

  private getLatestFwdIfEmpty(legIndex: number): void {
    const legComponent = this.legComponentsArray[legIndex];
    const form = legComponent.form;
    const fwdBidCtrl = form.get('forwardPointsBid');
    const fwdAskCtrl = form.get('forwardPointsAsk');

    if (fwdBidCtrl && fwdAskCtrl) {
      if (
        !Utilities.isNotBlank(fwdBidCtrl.value) &&
        !Utilities.isNotBlank(fwdAskCtrl.value)
      ) {
        legComponent.legFwdVolService.calcForwardPoint();
      }
    }
  }

  private reValidateRfq(hedgesValidation = false): void {
    this.rfqTabService.changeTabTile(RFQ_STATUS_WAITING, null);
    this.validationSub?.unsubscribe();
    this.validationSub = this.validateRfq().subscribe({
      next: _ => {
        this.rfqTabService.changeTabTile(null, null);
        if (hedgesValidation) {
          this.rfqTabService.setupMultiHedgesValidating = false;
        }
      },
      error: error => {
        this.rfqTabService.blockTabError(error);
      }
    });
  }

  private reSyncLegs(): void {
    this.legComponentsArray.forEach((leg, legIndex: number) => {
      leg.legSpotStrikeService.subToSpotChanges(legIndex);
      leg.legFwdVolService.subTofwdPointsChanges();
    });
  }

  private cancelAllApiCalls(): void {
    this.legsApiCallSub?.unsubscribe();
    this.validationSub?.unsubscribe();
    this.pricingSub?.unsubscribe();
  }

  private qs(q: string, elemToQuery?: HTMLElement): HTMLElement {
    const rootElem =
      elemToQuery || (this.elementRef.nativeElement as HTMLElement);
    return rootElem.querySelector(q) as HTMLElement;
  }

  private handleStaticNewLegs(index: number): void {
    if (this.tabData.static) {
      this.totalLegsLoaded++;
      this.saveDimensions.emit(this.tabData);
      if (this.totalLegsLoaded === this.tabData.legs.length) {
        if (this.tabData.title.productName.shortName === 'dcd') {
          this.legComponentsArray.forEach((leg: LegComponent) => {
            // dcd no longer needs valiadtion in rfq
            leg.form.setValidators([]);
            leg.form.updateValueAndValidity();
          });
          this.setDcdCurrencyPair();
        }
        this.currentCopyOfLegs = this.tabData.legs.map(
          x => x.leg
        ) as GenericLeg[];
        this.rfqTabService.selectedLegs = this.legComponentsArray;
        if (
          !(
            this.tabData.isVolBased ||
            this.tabData.isPremiumBasedVanillasWithSpotFeed
          )
        ) {
          this.legService.activeAttributes.set(
            this.legService
              .activeAttributes()
              .filter(
                x => x.shortName !== 'spot' && x.shortName !== 'secondSpot'
              )
          );
        }
        this.rfqTabService.setReadOnlyOnFields(
          false,
          this.rfqExecutionSettings.editableSettings.editableFields
        );
        const optionType = this.currentCopyOfLegs[0].optionType;
        const cross = this.currentCopyOfLegs[0].cross;

        if (cross) {
          this.onCurrencyPairChange(!!this.tabData.presetSetupData?.setup);
        }

        if (optionType) {
          this.rfqSetupComponent.onOptionTypeChange(
            optionType,
            !!this.tabData.presetSetupData?.setup
          );
        }
      }
    }
  }

  private handleAllowEditableFields(): void {
    if (this.configService.userSettings().useWhiteLabelMargin) {
      this.rfqExecutionSettings.editableSettings.editableFields.push(
        ...marginEditableFields
      );
    }

    if (this.rfqTab.isVolBased) {
      this.rfqExecutionSettings.editableSettings.editableFields.push(
        ...volBasedEditableFields
      );

      this.rfqExecutionSettings.editableSettings.lockWhileActive.push(
        ...volBasedEditableFields.filter(field => field !== 'optionType')
      );
    }

    if (
      this.rfqTab.static &&
      (this.tabData.isVolBased ||
        this.tabData.isPremiumBasedVanillasWithSpotFeed)
    ) {
      this.rfqExecutionSettings.editableSettings.editableFields.push(
        ...volBasedSpotFeedEditableFields
      );
    }
  }

  private async handleApiCalls(
    shouldCreateDto: boolean,
    marketFixingValueChanged: boolean,
    isDcd: boolean,
    hasDatesChanged: boolean,
    changeInCross: boolean
  ) {
    if (!isDcd && hasDatesChanged) {
      this.rfqSetupComponent.onDatesChange(this.blockVolBasedPriceHedgeChanges);
      await sleep(250);
    }

    this.cancelAllApiCalls();

    if (!isDcd) {
      this.rfqService.setRfqTabGenericInfo(
        this.tabData,
        legComponentsToRfqBuilderLegs(this.legComponentsArray),
        true
      ); // very important as set firstgen flag
    }

    this.legsApiCallSub = this.initApiCalls(
      shouldCreateDto,
      marketFixingValueChanged,
      changeInCross
    ).subscribe({
      next: _ => {
        if (isDcd) {
          this.handleDcdChildLegChanges();
          this.previousChildLegs = this.legComponentsArray.map(
            x => x.leg.pricedLeg.childVanillaOption
          );
        }
        this.blockVolBasedPriceHedgeChanges = false;
        this.rfqTabService.legsValid = true;
        this.rfqTabService.rfqRunning.update(_ => false);
        this.rfqTabService.changeTabTile(null, null);
      },
      error: error => {
        console.error('handleApiCalls', error);
        if (error.message) {
          this.rfqTabService.blockTabError(error.message);
        } else {
          this.rfqTabService.blockTabError(error);
        }
      }
    });
  }

  private removeIndicativeLoading(): void {
    this.rfqTabService.indicativeQuotes.update(state => {
      state.offer.loading = false;
      state.bid.loading = false;
      return state;
    });
  }

  private onCurrencyPairChange(blockSetValue: boolean): void {
    if (
      this.configService.userSettings().isWhiteLabelUser &&
      !this.strategyPriced &&
      this.tabData.static
    ) {
      /*
      Getting the primeBroker requires sending an account,
      but in the isWhiteLabelUser case the accounts is only
      available when the legs are ready for RFQing so we have to
      wait for the data to be fetched first
      */
      return;
    }
    this.rfqSetupComponent.onCurrencyPairChange(blockSetValue);
    this.rfqPricesComponent.setInfoBasedOnQuotePair();
  }

  private get isChangeInMarketFixing(): boolean {
    const field = this.legComponentsArray[0].form.get('cutOff')
      ? 'cutOff'
      : 'fixingSource';

    if (!this.strategyPriced?.products[0][field]) {
      return true;
    }

    const marketFixing = this.legComponentsArray[0].form.get(field).value;

    const prevMarketFixing =
      this.productsConfigService.cutOffTimesAndFixingSources[field + 's'].find(
        X => X.code === this.strategyPriced?.products[0][field]
      );

    return marketFixing !== prevMarketFixing.longName;
  }

  private initializeSalesMargin(): Observable<Margin[]> {
    if (this.salesMarginInitialized) {
      return of(null);
    }
    const useDefaultSalesMargin =
      !!this.configService.userSettings().parent.useDefaultSalesMargin;
    if (!useDefaultSalesMargin) {
      const nLegs = this.legComponentsArray.length;
      this.salesMarginInitialized = true;
      const margins: Margin[] = Array.from({ length: nLegs }, () => {
        return {
          marginStyle: 'Percent Base',
          margin: 0
        };
      });
      return of(margins);
    }

    return this.rfqService
      .tradeService<Margin[]>({
        task: 'getDefaultSalesMargin',
        products: this.strategyPriced?.products || []
      })
      .pipe(
        tap(response => {
          if (response?.margins) {
            this.salesMarginInitialized = true;
            this.updateSalesMargin(response.margins);
          }
        })
      );
  }

  private updateSalesMargin(salesMargin: Margin[]): void {
    if (Array.isArray(salesMargin)) {
      salesMargin.forEach((responseMargin, index) => {
        const legComponent = this.legComponentsArray[index];
        if (legComponent) {
          const formattedMargin =
            responseMargin.marginStyle === 'Percent Base' ||
            responseMargin.marginStyle === 'Percent Term'
              ? `${(responseMargin.margin * 100).toFixed(3)}%`
              : `${responseMargin.margin}`;

          legComponent.form
            .get('marginStyle')
            ?.setValue(responseMargin.marginStyle || 'None', {
              emitEvent: false
            });

          onMarginStyleChange(
            legComponent.form,
            responseMargin.marginStyle,
            legComponent.leg,
            true
          );

          legComponent.form.get('margin')?.setValue(formattedMargin);
        }
      });
    }
  }

  private initApiCalls(
    shouldCreateDto: boolean,
    marketFixingValueChanged: boolean,
    changeInCross: boolean
  ) {
    const isDcd = this.tabData.title.productName?.shortName === 'dcd';

    return (isDcd ? this.priceLegApi() : of(null)).pipe(
      switchMap(() => this.rfqTabService.setFormattedLegs),
      switchMap(() =>
        this.rfqApiCalls(
          shouldCreateDto,
          marketFixingValueChanged,
          changeInCross
        )
      ),
      switchMap(() =>
        forkJoin({
          validateRfq: this.validateRfq(true),
          salesMargin: of(null)
          // Temp disabled: CACIB issues
          // salesMargin: this.initializeSalesMargin()
        })
      ),
      tap(({ salesMargin }) => {
        if (salesMargin) {
          this.updateSalesMargin(salesMargin);
        }
      })
    );
  }

  private listenToHedgesChanges(): void {
    if (this.shouldValidateHedges) {
      this.subSink.sink = this.rfqTabService.changeInHedges.subscribe(_ => {
        if (
          this.rfqSetupComponent?.multiHedgesValid() ||
          this.rfqSetupComponent?.getHedges().hedges.length === 0
        ) {
          this.onHedgeChange();
        }
      });
    }
  }

  private get shouldValidateHedges(): boolean {
    return (
      this.tabData.isVolBased &&
      this.tabData.executionContext.enableMultipleHedges
    );
  }

  private async onHedgeChange() {
    if (this.blockVolBasedPriceHedgeChanges) {
      return;
    }
    await sleep(250);
    if (this.rfqTabService.rfqRunning()) {
      this.handleUpdateNewVolBasedQuoteListeners().subscribe();
      return;
    }
    this.updateVolBasedQuoteListeners = true;
    this.reValidateRfq(true);
  }

  private validateRfq(
    priceLegs: boolean = false
  ): Observable<VolBasedRfqOptionPriceV2Result> {
    this.updatePricingMessages(false, false, '');
    const isDcd = this.tabData.title.productName?.shortName === 'dcd';
    if (!this.strategyPriced) {
      this.onChangeInLegs();
      this.rfqTabService.changeTabTile(null, null);
      return EMPTY;
    }

    const payload = {
      products: this.strategyPriced.products,
      hedgeType: this.hedge,
      providers: this.providers
    };

    if (this.shouldValidateHedges) {
      payload['hedges'] = this.rfqSetupComponent.getHedges().hedges;
    }

    return this.rfqService.validateRfq(payload).pipe(
      switchMap((data: ValidateRfq) => {
        const response = data.response;
        if (!response.isPricingAllowed) {
          this.updatePricingMessages(false, true, response.msg);
          return EMPTY;
        } else {
          if (!isDcd && priceLegs) {
            this.pricingSub = this.priceLegApi().subscribe();
            this.rfqService.setRfqTabGenericInfo(
              this.tabData,
              legComponentsToRfqBuilderLegs(this.legComponentsArray),
              true
            );
          }

          if (response.isRfqValid && response.isWarning) {
            this.updatePricingMessages(
              true,
              false,
              response.warningMessages.join('\n')
            );
          } else if (!response.isRfqValid) {
            this.updatePricingMessages(false, true, response.msg);
            return EMPTY;
          }
        }

        addRemoveVolSpread(
          response.allowVolMargin,
          this.tabData.useWhiteLabelMargin,
          this.legComponentsArray
        );

        const discountCurrenciesLoaded = this.legComponentsArray.every(leg => {
          const discountFactorCurrency = leg.form.get('discountFactorCurrency');
          return !discountFactorCurrency || discountFactorCurrency.value;
        });

        if (
          response.isRfqValid &&
          this.tabData.isVolBased &&
          (this.updateVolBasedQuoteListeners ||
            !this.currVolBasedOptionProducts ||
            hasFieldsChanged(
              this.strategyPriced,
              this.currVolBasedOptionProducts
            )) &&
          discountCurrenciesLoaded
        ) {
          return this.handleUpdateNewVolBasedQuoteListeners();
        }
        return of(null);
      })
    );
  }

  private rfqApiCalls(
    shouldCreateDto: boolean,
    marketFixingValueChanged: boolean,
    changeInCross: boolean
  ): Observable<InitRfqApiCalls> {
    const isDcd = this.tabData.title.productName.shortName === 'dcd';
    return this.getDFCall().pipe(
      switchMap(_ => this.rfqTabService.setFormattedLegs),
      switchMap(_ => {
        shouldCreateDto = this.shouldCreateDto(isDcd, shouldCreateDto);
        return forkJoin({
          marketFixing: marketFixingValueChanged
            ? this.rfqTabService.marketCodeFixingSource()
            : of(null),
          createDtoCall: shouldCreateDto
            ? this.rfqTabService.createDto
            : of(null),
          getClientAccounts:
            changeInCross && this.configService.userSettings().isWhiteLabelUser
              ? this.rfqService.getClientAccounts(
                  this.rfqTabService.createProductsObject()
                )
              : of(null)
        }).pipe(tap(response => this.handleCreateFork(response)));
      }),
      catchError(error => {
        this.strategyPriced = null;
        return throwError(error);
      })
    );
  }

  private handleCreateFork(response: InitRfqApiCalls): void {
    if (response.getClientAccounts) {
      const accountEmpty = !this.rfqTabService.accounts().length;
      this.rfqTabService.accounts.update(
        _ => response.getClientAccounts.result
      );
      this.rfqSetupService.setAccountsNames(this.rfqTabService.accounts());
      this.rfqSetupService.isAccountPresent();
      if (accountEmpty) {
        this.rfqSetupComponent.setDefaultAccount();
      }
      this.onCurrencyPairChange(false);
    }

    if (response.marketFixing) {
      this.rfqTabService.medusaCodeAndType = response.marketFixing;
    }

    if (response.createDtoCall) {
      this.rfqTabService.createDtoResponse = clone(
        response.createDtoCall.result,
        true
      );

      this.prevRfqProductApiName = rfqProductApiName(
        this.rfqTabService.formattedDtoLegs,
        this.tabData.title.productName.shortName
      );
    }
    this.rfqTabService.setDetailsDtoMap();
  }

  private shouldCreateDto(isDcd: boolean, shouldCreateDto: boolean): boolean {
    const currentProductName = rfqProductApiName(
      this.rfqTabService.formattedDtoLegs,
      this.tabData.title.productName.shortName
    );

    return (
      shouldCreateDto ||
      (isDcd &&
        this.legComponentsArray[0].leg.pricedLeg.childVanillaOption.cross !==
          this.previousChildLegs?.[0].cross) ||
      !this.rfqTabService.createDtoResponse ||
      this.prevRfqProductApiName !== currentProductName
    );
  }

  private getDFCall(): Observable<DiscountFactorResponse[]> {
    return this.tabData.isVolBased
      ? forkJoin(
          this.legComponentsArray.map((x, index) =>
            canDisplayDF(x.leg.productType) &&
            (!x.form.get('discountFactor')?.value ||
              !x.form.get('discountFactorCurrency')?.value)
              ? this.rfqTabService.discountFactor(
                  this.strategyPriced.products[index]
                )
              : of(null)
          )
        ).pipe(
          tap((discountFactors: DiscountFactorResponse[]) => {
            discountFactors.forEach(
              (df: DiscountFactorResponse, index: number) => {
                if (df) {
                  this.handleVolBasedOnLegComponents(df, index);
                  this.rfqSetupComponent.removeUSDPremium();
                }
              }
            );
          })
        )
      : of(null);
  }

  private hasDatesChangedFn(
    strategyToUse: GenericLeg[],
    prevStrategy: GenericLeg[]
  ): boolean {
    return (
      !prevStrategy ||
      strategyToUse?.length !== prevStrategy?.length ||
      (prevStrategy as any[]).find(
        (product, index: number) =>
          product.settlementDate !== strategyToUse[index].settlementDate ||
          product.expiryDate !== strategyToUse[index].expiryDate ||
          product.redemptionDate !== strategyToUse[index].redemptionDate ||
          product.firstFixingDate !== strategyToUse[index].firstFixingDate ||
          product.lastFixingDate !== strategyToUse[index].lastFixingDate
      )
    );
  }

  private shouldCreateDtoFn(
    previousProduct: string,
    prevCross: string
  ): boolean {
    // determines if a createDto call should be made
    const currentProduct = this.tabData.title.productName?.shortName;
    if (currentProduct === 'dcd') {
      return false;
    }
    return (
      this.tabData.static ||
      !isEqual(previousProduct, currentProduct) || // change in product
      !isEqual(this.strategyPriced.products[0].cross, prevCross) // change in currencyPair
    );
  }

  private get getStrategy(): Payload {
    const reportingCurrency = this.userStorageService.getItem<string>(
      ReportingCurrencyStateKey
    ); // get the stored reporting currency

    let task = {
      taskType: 'price',
      reportingCurrency: reportingCurrency || 'USD', // if none is set, use USD
      taskParameters: {
        output: ['Price', 'Gamma', 'Vega', 'SpotDelta'],
        calculatePortfolio: true
      },
      portfolioPremiumCurrency:
        this.rfqTabService.executionContextType() === 'genericMultiHedge'
          ? this.rfqSetupService.setupForm.get('premium').value
          : undefined
    };

    return this.legService.createServicePayload(
      this.pricingExtraService.getMarketDataTimeStamp,
      this.pricingExtraService.marketDataSpreadingRule,
      this.currentCopyOfLegs.map((product, index: number) => {
        return {
          product,
          attributes: this.legComponentsArray[index].leg.attributes
        };
      }),
      task,
      {
        treasuryRates: [],
        fixings: [],
        marketData: []
      }
    );
  }

  private indicativeQuotesLoading(): void {
    if (this.rfqTabService.rfqRunning()) {
      return;
    }
    this.rfqTabService.indicativeQuotes.update(state => {
      state.offer.loading = true;
      state.bid.loading = true;
      return state;
    });
  }

  private priceLegApi(): Observable<LegPrice> {
    this.indicativeQuotesLoading();
    return this.legService.priceLeg(this.strategyPriced).pipe(
      tap((response: LegPrice) => {
        this.handlePricingResponse(response, this.strategyPriced);
        this.rfqTabService.pricingResult.set(response);
      }),
      catchError(x => {
        this.removeIndicativeLoading();
        return throwError(x);
      })
    );
  }

  private setRfqTabLegs(): void {
    const rfqTabClone = clone(this.tabData);
    rfqTabClone.legs = rfqLegs(
      legComponentsToRfqBuilderLegs(this.legComponentsArray),
      this.tabData.static,
      false,
      this.legService.productSpecifications
    );
    Object.assign(this.tabData, rfqTabClone);
  }

  private setRfqTabQuotePair(currencyPair?: QuotePair): void {
    const rfqTabClone = clone(this.tabData);
    rfqTabClone.title.quotePair =
      currencyPair ||
      getTabTitleCross(
        legComponentsToRfqBuilderLegs(this.legComponentsArray)[0]
      );
    Object.assign(this.tabData, rfqTabClone);
  }

  private setRfqTabProductName(): ProductName {
    const rfqTabClone = clone(this.tabData);
    const prevProduct = rfqTabClone.title.productName?.shortName;
    rfqTabClone.title.productName = rfqTabTitleMapper(
      legComponentsToRfqBuilderLegs(this.legComponentsArray),
      this.configService.userSettings()
    );
    Object.assign(this.tabData, rfqTabClone);

    const productName = this.tabData.title.productName;

    if (productName && prevProduct !== productName.shortName) {
      this.rfqSetupComponent.removeUnavailableFields();
      this.rfqSetupComponent.hedgeRateStatus();
    }
    return productName;
  }

  private handleDcdChildLegChanges(): void {
    const currentChildLegs = this.resultPerLeg
      .map(x => x.childVanillaOption)
      .filter(y => y);

    const hasOptionTypeChanged = this.hasOptionTypeChangedFn(
      currentChildLegs,
      this.previousChildLegs
    );

    const hasDatesChanged = this.hasDatesChangedFn(
      currentChildLegs,
      this.previousChildLegs
    );

    if (hasOptionTypeChanged) {
      this.rfqSetupComponent.onOptionTypeChange();
    }

    if (hasDatesChanged) {
      this.rfqSetupComponent.onDatesChange();
    }
  }

  private handlePricingResponse(response: LegPrice, strategy: Payload): void {
    const isDcd = this.tabData.title.productName.shortName === 'dcd';
    this.portfolioResults = response.portfolioResult?.pricing;
    this.resultPerLeg = response.resultPerLeg;
    if (isDcd) {
      this.legComponentsArray.forEach((x, index: number) => {
        x.legHelperService.legStrategy = strategy;
        x.leg.pricedLeg = response.resultPerLeg[index];
        x.legPricingService.handlePricingResults(response);
      });
      this.rfqService.setRfqTabGenericInfo(
        this.tabData,
        legComponentsToRfqBuilderLegs(this.legComponentsArray),
        false
      ); // update the tab before pricing
    }

    if (
      (this.tabData.isPremiumBasedVanillasWithSpotFeed ||
        this.tabData.isVolBased) &&
      this.legInputChangeMade &&
      !this.legInputsFocused &&
      this.rfqTabService.legsValid &&
      this.rfqTabService.rfqRunning()
    ) {
      // we don't want apis to be called on every key change
      // only make calls when there is a change in the leg and an input made
      this.legInputChangeMade = false;
      this.rfqTabService.formatVolBasedQuote();
    }
    const setupValue = this.rfqSetupService.setupForm.value;
    this.createIndicativeQuotes(setupValue);
    this.removeIndicativeLoading();
  }

  public premiumStyleChange(): void {
    if (this.rfqTab.isVolBased) {
      this.handleUpdateNewVolBasedQuoteListeners().subscribe();
    }
  }

  public createIndicativeQuotes(
    values: RfqSetup,
    fromVolBasedV2Call = false
  ): void {
    if (this.rfqTabService.rfqRunning()) {
      return;
    }
    if (fromVolBasedV2Call) {
      this.createV2IndicativeQuotes();
    }

    if (this.tabData.isVolBased) {
      return;
    }

    const base = this.tabData.title.quotePair?.base;
    const isDcd = this.tabData.title.productName?.shortName === 'dcd';

    if (!values.premiumValue) {
      console.warn('missing a premium value for indicative price');
    }

    if (!values.premium) {
      console.warn('missing a premium for indicative price');
    }

    if (!this.portfolioResults) {
      console.warn('missing a portfolioResults for indicative price');
    }

    if (!base) {
      console.warn('missing a base for indicative price');
    }

    if (
      !values.premiumValue ||
      !values.premium ||
      !this.portfolioResults ||
      !base
    ) {
      this.rfqTabService.indicativeQuotes.update(state => {
        state.bid.quote = null;
        state.offer.quote = null;
        return state;
      });
      return;
    }

    this.rfqTabService.indicativeQuotes.update(state => {
      const offerSide = isDcd ? 'bid' : 'offer';
      if (this.tabData.positions[offerSide]) {
        state[offerSide].quote = indicativeQuoteMaker(
          'ask',
          this.portfolioResults,
          values.premium,
          base,
          values.premiumValue
        );
      }

      if (!isDcd && this.tabData.positions.bid) {
        state.bid.quote = indicativeQuoteMaker(
          'bid',
          this.portfolioResults,
          values.premium,
          base,
          values.premiumValue
        );
      }
      return state;
    });
  }

  public onHedgePremiumChange(
    value: string,
    fieldChanged: 'hedge' | 'premium'
  ): void {
    if (this.rfqTabService.rfqRunning()) {
      return;
    }
    this.createIndicativeQuotes({
      ...this.rfqSetupService.setupForm.value,
      [fieldChanged]: value
    });

    const hedgeToUse =
      fieldChanged === 'hedge'
        ? value
        : this.rfqSetupService.setupForm.value.hedge;
    if (hedgeToUse !== this.hedge) {
      this.hedge = hedgeToUse;
      this.reValidateRfq();
    }

    if (
      fieldChanged === 'premium' &&
      this.rfqTabService.executionContextType() === 'genericMultiHedge' &&
      this.rfqSetupService.showPanel()
    ) {
      this.strategyPriced.task.portfolioPremiumCurrency = value;
      this.priceLegApi().subscribe();
    }
  }

  @Throttle(500)
  public get nonDxExists(): boolean {
    return (
      this.rfqSetupService.setupForm.get('isDx') &&
      !this.rfqSetupService.setupForm.get('isDx').value
    );
  }

  private wipeAllIndicativeQuotes() {
    this.rfqTabService.indicativeQuotes.update(state => {
      state.bid.loading = false;
      state.bid.quote = null;
      state.offer.loading = false;
      state.offer.quote = null;
      return state;
    });
  }

  private wipeQuotes(): void {
    this.rfqTabService.quotes.bids = [];
    this.rfqTabService.quotes.offers = [];
    this.rfqTabService.frozenQuotes = null;
  }

  private updatePricingMessages(
    isWarning: boolean,
    isError: boolean,
    messages: string
  ): void {
    this.rfqPricingMessages.isWarning = isWarning;
    this.rfqPricingMessages.messages = messages;
    this.rfqPricingMessages.isError = isError;
    this.rfqPricingMessages = clone(this.rfqPricingMessages);

    if (isError) {
      this.rfqTabService.changeTabTile(RFQ_STATUS_ERROR, null);
    }
  }

  private get makeCopyOfLegs(): GenericLeg[] {
    // returns a copy of the legs without the position being 'Two-way'
    return this.legComponentsArray.map(legComponent => {
      return {
        ...legComponent.form.value,
        position: legComponent.leg.position
      };
    });
  }

  private handleVolBasedOnLegComponents(
    response: DiscountFactorResponse,
    index: number
  ) {
    const legComponent = this.legComponentsArray[index];
    const form = legComponent.form;

    const discountFactorCurrency = form.get('discountFactorCurrency');
    const discountFactor = form.get('discountFactor');
    discountFactorCurrency.setValue(response.currency, {
      emitEvent: false
    });

    if (!discountFactor.value) {
      discountFactor.setValue(
        this.percentPipe.transform(
          response.spotToForwardDiscountFactor[response.currency],
          '1.4-4'
        ),
        { emitEvent: false }
      );
    }
  }

  /**
   * listens to changes in an amount attribute and adds the corresponding currency to the end of it
   */
  private rfqPremiumMarginChangeListener(legComponent: LegComponent) {
    const field = 'rfqPremiumMargin';
    legComponent.legHelperService.subSink.sink = legComponent.form
      .get(field)
      .valueChanges.subscribe(() => {
        const ccy = this.rfqSetupService.setupForm.get('premium')?.value;
        if (legComponent.form.get(field).value) {
          Utilities.endOfTextBoxAdder(
            legComponent.legHelperService.qs('#' + field),
            legComponent.form.get(field),
            ' ' + ccy,
            !!ccy,
            'currency',
            this.renderer
          );
        }
      });
  }

  /**
   *
   */

  private listenToFirstLeg(): void {
    this.firstLegListener.unsubscribe();
    const leg1Form = this.legComponentsArray[0]?.form;
    if (!leg1Form) {
      return;
    }
    this.firstLegListener = new SubscriptionSink();
    this.subSink.sink = this.firstLegListener;
    this.firstLegListener.sink = leg1Form
      .get('cross')
      ?.valueChanges.pipe(startWith(leg1Form.get('cross').value))
      .subscribe(cross => {
        this.rfqTabService.createDtoResponse = null;
        if (
          cross &&
          (!this.tabData.static ||
            this.totalLegsLoaded === this.tabData.legs.length)
        ) {
          this.setRfqTabQuotePair();
          this.onCurrencyPairChange(false);
        }
      });

    this.firstLegListener.sink = leg1Form
      .get('optionType')
      ?.valueChanges.pipe(startWith(leg1Form.get('optionType').value))
      .subscribe(optionType => {
        if (optionType) {
          this.rfqSetupComponent.onOptionTypeChange(optionType);
        }
      });

    this.firstLegListener.sink = leg1Form
      .get('alternativeCurrency')
      ?.valueChanges.subscribe(_ => {
        if (
          !this.tabData.static ||
          this.totalLegsLoaded === this.tabData.legs.length
        ) {
          this.setDcdCurrencyPair();
        }
      });
  }

  private setDcdCurrencyPair(): void {
    const cross = this.legComponents.first.leg.usableCross;
    if (cross) {
      this.setRfqTabQuotePair({
        base: cross.substring(0, 3),
        term: cross.substring(3, 6)
      });
      this.onCurrencyPairChange(false);
    }
  }
  private setupVolBasedMarginListeners(
    legComponent: LegComponent,
    field1: string,
    field2: string
  ) {
    const form = legComponent.form;

    // blank field "a" if "b" is filled
    const blankFormField = (a: string, b: string) => {
      return () => {
        if (form.get(b)?.value && form.get(a)?.value) {
          return form.get(a).setValue('', { emitEvent: false });
        }
      };
    };

    const updatePricing = () => {
      return () => {
        if (!this.rfqTabService.rfqRunning() || !form.valid) {
          return;
        }
        this.rfqTabService.handleClientQuoteEventUsingLast('updatePricing');
      };
    };

    legComponent.legHelperService.subSink.sink = form
      .get(field1)
      .valueChanges.subscribe(blankFormField(field2, field1));

    legComponent.legHelperService.subSink.sink = form
      .get(field1)
      .valueChanges.pipe(
        filter(() => this.rfqTabService.rfqRunning()),
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe(updatePricing());
  }

  /**
   *
   */

  private setPositionHeaders(): void {
    setTimeout(_ => {
      const rfqTabClone = clone(this.tabData);
      rfqTabClone.positions = positionsHandler(
        this.tabData.title.productName?.shortName,
        legComponentsToRfqBuilderLegs(this.legComponentsArray)
      );
      Object.assign(this.tabData, rfqTabClone);
    }, 0);
  }

  private hasOptionTypeChangedFn(
    updatedLegs: GenericLeg[],
    previousLeg: GenericLeg[]
  ): boolean {
    /* determines if there has been a change that would
    require the setup screen to be updated */
    return (
      !previousLeg?.length ||
      updatedLegs[0].optionType !== previousLeg[0].optionType
    );
  }

  private listenToInputs(index: number): void {
    const legComponent = this.legComponentsArray[index];
    legComponent.legHelperService.subSink.sink =
      legComponent.form.valueChanges.subscribe(_ => {
        if (this.legInputsFocused) {
          this.wipeQuotes();
        }
      });

    this.setPositionHeaders();

    legComponent.legHelperService.subSink.sink = legComponent.form
      .get('cross')
      ?.valueChanges.subscribe(_ => {
        const form = legComponent.form;
        form.get('discountFactorCurrency')?.setValue('');
        form.get('discountFactor')?.setValue('');
      });

    const legPosition = legComponent.form.get('position');

    if (index === 0) {
      this.updatePositionInLegs();
    }

    if (legPosition) {
      legComponent.leg.position = legPosition.value;
      legComponent.legHelperService.subSink.sink =
        legPosition.valueChanges.subscribe(val => {
          this.setPositionHeaders();
          if (this.legInputsFocused) {
            this.tabData.oneWayView.oneWay = true;
            legComponent.leg.position = val;
          }
        });
    }

    legComponent.legHelperService
      .qsa('input')
      .forEach((element: HTMLElement) => {
        this.listenToElement(element);
      });

    const marginStyleCtrl = legComponent.form.get('marginStyle');
    const marginStyleAttr = legComponent.leg.attributesAsFormControls.find(
      ctrl => ctrl.key === 'marginStyle'
    );

    legComponent.legHelperService.subSink.sink =
      marginStyleCtrl?.valueChanges.subscribe(val => {
        this.storeAttributeValue('marginStyle', val);
        if (this.configService.userSettings().useWhiteLabelMargin) {
          marginStyleAttr.required = !!val;
          marginStyleAttr.required
            ? marginStyleCtrl.addValidators(Validators.required)
            : marginStyleCtrl.removeValidators(Validators.required);
          marginStyleCtrl.updateValueAndValidity({ emitEvent: false });
        }
      });
    this.optionTypeListener(legComponent);
    if (this.tabData.isVolBased) {
      this.setupVolBasedMarginListeners(
        legComponent,
        'rfqPremiumMargin',
        'rfqVolMargin'
      );
      this.setupVolBasedMarginListeners(
        legComponent,
        'rfqVolMargin',
        'rfqPremiumMargin'
      );

      this.rfqPremiumMarginChangeListener(legComponent);
    }
  }

  private optionTypeListener(legComponent: LegComponent): void {
    legComponent.legHelperService.subSink.sink = legComponent.form
      .get('optionType')
      ?.valueChanges.subscribe(_ => {
        if (this.rfqTab.isVolBased && this.rfqTabService.rfqRunning()) {
          this.handleUpdateNewVolBasedQuoteListeners().subscribe();
        }
      });
  }

  private storeAttributeValue(field: string, val: string): void {
    const rfqSavedSettings = this.rfqSettingsState();
    if (!rfqSavedSettings['leg-attributes']) {
      rfqSavedSettings['leg-attributes'] = {};
    }
    rfqSavedSettings['leg-attributes'][field] = val;
    this.userStorageService.setItem(RfqSettingsStateKey, rfqSavedSettings);
  }

  private rfqSettingsState(): RfqSettingsState {
    return (
      this.userStorageService.getItem<RfqSettingsState>(RfqSettingsStateKey) ||
      {}
    ); // if there are settings load them
  }

  private listenToElement(element: HTMLElement) {
    element.addEventListener('blur', this.elemLegInputBlur.bind(this));
    element.addEventListener('focus', this.elemLegInputFocus.bind(this));
    element.addEventListener('input', this.elemInputChangeMade.bind(this));
  }

  private elemInputChangeMade() {
    this.legInputChangeMade = true;
  }

  private elemLegInputFocus() {
    this.legInputsFocused = true;
  }

  private elemLegInputBlur() {
    this.legInputsFocused = false;
  }

  private hideSpots(): void {
    const activeAttributes = this.legService.activeAttributes();
    this.legService.activeAttributes.set(
      activeAttributes.filter(attribute => {
        return (
          attribute.shortName !== 'spot' && attribute.shortName !== 'secondSpot'
        );
      })
    );
    // remove the spot and second spots and positions if needed (more than 1 leg)
    const currentLeg = this.legService.legs[0];
    if (!currentLeg?.attributesAsFormControls) {
      return;
    }
    currentLeg.attributesAsFormControls
      .filter(control => ['spot', 'secondSpot'].includes(control.key))
      .forEach(spot => (spot.display = false));

    this.legComponentsArray.forEach(x => {
      x.form.get('spot')?.disable();
      x.form.get('secondSpot')?.disable();
    });
  }

  private updatePositionInLegs(): void {
    // adds two-way or removes it from the first rfq leg
    if (this.tabData.title.productName?.shortName === 'dcd') {
      return;
    }
    const legComponent = this.legComponentsArray[0];
    const position = legComponent?.form.get('position');
    const currentPosition = legComponent?.leg.position;
    let positionToUse = setPositionToUse(
      legComponentsToRfqBuilderLegs(this.legComponentsArray),
      this.configService.userSettings()
    );
    if (!currentPosition) {
      return;
    }
    if (!positionToUse || this.rfqTab.oneWayView.oneWay) {
      positionToUse =
        this.rfqTab.oneWayView.oneWay || this.legComponentsArray.length > 1
          ? legComponent.leg.position
          : position.value
            ? this.twoWayText
            : '';
    }
    position?.setValue(positionToUse);
  }

  private get rfqTab(): RfqTab {
    return this.rfqTabService.tabData as RfqTab;
  }

  private handleTabInit(): void {
    this.legSyncingInfo.spot = !this.tabData.executionContext.useSpotInRfqPanel;
    this.legService.attributesDisplayList =
      this.productsConfigService.addExtraRfqAttributesToList(
        false,
        this.tabData.isVolBased,
        this.tabData.isVolBased,
        this.configService.userSettings(),
        true
      );

    this.rfqTabService.tabData = this.tabData;
    if (
      this.tabData.static &&
      !(
        this.tabData.isPremiumBasedVanillasWithSpotFeed ||
        this.tabData.isVolBased
      )
    ) {
      this.hideSpots();
    }

    if (this.tabData.isVolBased) {
      this.legSyncingInfo.premiumSettlementDate = true;
      this.legSyncingInfo.premiumValueType = true;
    }

    this.tabData.legs?.forEach(leg => {
      if (
        this.tabData.isVolBased &&
        !(leg.leg.premiumValueType && this.rfqTab.presetSetupData?.setup)
      ) {
        leg.leg.premiumValueType = 'Spot';
      }
      this.addProduct(leg.leg.productType, false, leg.leg);
    });
    this.legs = this.legService.legs;
    this.productGroupings = clone(
      this.productsConfigService.rfqProductsGroupings
    );
    this.rfqExecutionSettings.static = this.tabData.static;
    this.handleAllowEditableFields();
  }

  @Debounce(500)
  private debouncedUpdateNewVolBasedQuoteListeners() {
    this.handleUpdateNewVolBasedQuoteListeners().subscribe();
  }

  private createV2IndicativeQuotes() {
    const v2Response =
      this.rfqTabService.lastVolBasedRfqOptionPriceV2Response();
    const quotes = this.rfqTabService.setNewVolPremiums(
      v2Response.portfolioResult,
      v2Response.results.resultPerLeg,
      'forwardPremium',
      v2ToQuoteMap(
        v2Response,
        this.rfqSetupService.setupForm.value as any
      ) as any,
      null,
      true
    );

    this.rfqTabService.indicativeQuotes.update(state => {
      state.bid.loading = false;
      state.bid.quote = quotes.quoteBid;
      state.offer.loading = false;
      state.offer.quote = quotes.quoteOffer;
      return clone(state);
    });
  }

  private handleUpdateNewVolBasedQuoteListeners(): Observable<VolBasedRfqOptionPriceV2Result> {
    this.indicativeQuotesLoading();
    return this.updateNewVolBasedQuoteListeners().pipe(
      tap(r => {
        this.rfqTabService.updateNewVolBasedQuoteListeners(r);
        this.updateVolBasedQuoteListeners = false;
        this.rfqTabService.lastVolBasedRfqOptionPriceV2Response.set(r);
        const setupValue = this.rfqSetupService.setupForm.value;
        const currentTime = dayjs.utc().valueOf();
        this.rfqTabService.lastQuoteEvents().forEach(quote => {
          if (quote.quote.timelimit >= currentTime) {
            this.rfqTabService.updateQuoteFromV2(quote, r);
          }
        });
        this.createIndicativeQuotes(setupValue, true);
      }),
      catchError(_ => {
        this.updateVolBasedQuoteListeners = false;
        const setupHedges = clone(this.rfqSetupService.setup.getHedges());
        setupHedges.hedges.forEach(hedge => {
          hedge.notional = null;
          hedge.impliedVol = null;
          hedge.strike = null;
        });
        if (setupHedges.hedges.length) {
          this.rfqTabService.updateNewVolBasedQuoteListeners(setupHedges);
        }
        return of(null);
      })
    );
  }

  private updateNewVolBasedQuoteListeners(): Observable<VolBasedRfqOptionPriceV2Result> {
    const legComponents = this.rfqTabService.selectedLegs;
    if (!this.tabData.isVolBased || legComponents.find(x => !x.form.valid)) {
      return EMPTY;
    }
    const setup = this.rfqSetupService.setup;
    const { hedges } = setup.getHedges();
    const setupForm = setup.form.getRawValue();

    this.currVolBasedOptionProducts = this.legService.createProductsObject(
      legComponents.map(legComponent => {
        return {
          product: legComponent.form.getRawValue(),
          attributes: legComponent.leg.attributes
        };
      })
    );

    const premiumDate = dayjs(setupForm.premiumDate).format('YYYY-MM-DD');
    const executionContext = this.tabData.executionContext;

    const payload = {
      task: 'volBasedRfqOptionPrice_v2',
      premiumDate,
      spotBid: Utilities.cleanFormattedNumeric(setupForm.spots.spotBid),
      spotAsk: Utilities.cleanFormattedNumeric(setupForm.spots.spotAsk),
      premiumValue: setupForm.premiumValue,
      premiumCurrency: setupForm.premium,
      hedgeMode: setupForm.hedgeMode,
      hedges,
      premiumStyle: setupForm.premiumStyle,
      products: this.currVolBasedOptionProducts,
      comment: this.rfqTabService.rfqCommentSignal(),
      deltaHedgeRounding: executionContext.deltaHedgeRounding,
      enableDeltaHedgeRounding: executionContext.enableDeltaHedgeRounding,
      enablePremiumRounding: executionContext.enablePremiumRounding,
      premiumBpRounding: executionContext.premiumBpRounding,
      requestId: 'NONE' // XXXX
    };

    return this.rfqService.tradeService(payload, true);
  }

  private async getExecutionContext(): Promise<ExecutionContext> {
    const products =
      this.tabData.strategyBrokenDown || this.tabData.presetSetupData
        ? this.tabData.legs.map(leg => leg.leg)
        : this.legService.createProductsObject(
            this.tabData.legs.map(leg => {
              return {
                product: leg.leg,
                attributes: this.legService.getProductSpec(
                  leg.leg.productType.replace(/\s+/g, '').toLowerCase()
                ).attributes
              };
            })
          );

    return firstValueFrom(this.rfqService.getExecutionContext({ products }));
  }
}
